import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { getConsent, setConsent, initAnalytics } from "../lib/analytics";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";

export default function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const existing = getConsent();
    if (existing) return;
    if (navigator.globalPrivacyControl) {
      // Honor Global Privacy Control: auto-reject non-essential without nagging.
      setConsent({ analytics: false, marketing: false, gpc: true });
      return;
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    const reopen = () => {
      const c = getConsent() || { analytics: false, marketing: false };
      setPrefs({ analytics: !!c.analytics, marketing: !!c.marketing });
      setManage(true);
      setVisible(true);
    };
    window.addEventListener("dd:cookie-settings", reopen);
    return () => window.removeEventListener("dd:cookie-settings", reopen);
  }, []);

  const choose = (c) => {
    setConsent(c);
    setVisible(false);
    initAnalytics();
  };

  if (!visible) return null;
  return (
    <>
      {/* Flow spacer: the fixed bar must never cover page content, CTAs, or the footer */}
      <div aria-hidden="true" className={manage ? "h-72" : "h-24 sm:h-[72px]"} data-testid="cookie-consent-spacer" />
      <div className="fixed bottom-0 inset-x-0 z-[60] bg-slate-900 text-white border-t border-slate-700 shadow-2xl" role="region" aria-label={t("cookie.title")} data-testid="cookie-consent-banner">
        <div className="max-w-7xl mx-auto px-4 py-1.5 sm:py-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <p className="text-[11px] leading-tight sm:text-xs text-slate-300 flex-1">
              <Cookie size={14} className="inline-block text-emerald-400 mr-1.5 -mt-0.5" aria-hidden="true" />
              {t("cookie.barBody")}{" "}
              <a href="/privacy#cookies" className="underline hover:text-white">{t("cookie.privacyLink", "Privacy Policy")}</a>
            </p>
            <div className="flex gap-2 shrink-0 flex-col min-[380px]:flex-row min-[380px]:items-center [&>*]:w-full min-[380px]:[&>*]:w-auto">
              <Button size="sm" onClick={() => choose({ analytics: true, marketing: true })} className="bg-emerald-500 hover:bg-emerald-600 rounded-full min-h-[44px]" data-testid="cookie-accept-all">{t("cookie.acceptAll")}</Button>
              <Button size="sm" variant="outline" onClick={() => choose({ analytics: false, marketing: false })} className="rounded-full text-white border-slate-600 hover:bg-slate-800 min-h-[44px]" data-testid="cookie-reject">{t("cookie.reject")}</Button>
              {manage ? (
                <Button size="sm" variant="outline" onClick={() => choose(prefs)} className="rounded-full text-white border-slate-600 hover:bg-slate-800 min-h-[44px]" data-testid="cookie-save-preferences">{t("cookie.save")}</Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setManage(true)} className="rounded-full text-slate-300 min-h-[44px]" data-testid="cookie-manage">{t("cookie.manage")}</Button>
              )}
            </div>
          </div>
          {manage && (
            <div className="mt-3 pt-3 border-t border-slate-700" data-testid="cookie-manage-panel">
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked disabled className="accent-emerald-500" /> {t("cookie.essential")}
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={prefs.analytics} onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })} className="accent-emerald-500" data-testid="cookie-analytics-toggle" /> {t("cookie.analytics")}
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={prefs.marketing} onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })} className="accent-emerald-500" data-testid="cookie-marketing-toggle" /> {t("cookie.marketing")}
                </label>
              </div>
              <p className="text-[11px] text-slate-400 mt-2" data-testid="cookie-categories">{t("cookie.gpcNote")}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
